import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { LoginForm } from './LoginForm';
import { ProfileSelector } from './ProfileSelector';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useRoles } from '@/hooks/useRoles';
import { useQueryClient } from '@tanstack/react-query';
import { clearGroupsCache } from '@/hooks/useGroupChildren';
import { clearUserInfoCache } from '@/hooks/useUserInfo';
import { clearUnitsCache } from '@/lib/unitsCache';
import { clearRolesCache } from '@/lib/rolesCache';
import { clearDigitalAssetsCache } from '@/lib/digitalAssetsCache';
import { clearMessageBoardCache } from '@/lib/messageBoardCache';

import { clearUnitDetailCache } from '@/lib/unitDetailCache';
import canyonImage from '@/assets/login-canyon.jpg';
import forestImage from '@/assets/login-forest.jpg';
import coastImage from '@/assets/login-coast.jpg';

interface Profile {
  customerNumber?: string;
  name?: string;
  company?: string;
  userName?: string;
  id?: number;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading, setProfileToken } = useAuth();
  const { isFeatureEnabled, isLoading: isFlagsLoading } = useFeatureFlags();
  const { fetchRoles } = useRoles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [profileState, setProfileState] = useState<'idle' | 'loading' | 'selecting' | 'done'>(() => {
    if (sessionStorage.getItem('selectedProfile')) return 'done';
    if (localStorage.getItem('user')) return 'done';
    return 'idle';
  });
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [backgroundImage] = useState(() => {
    const images = [canyonImage, forestImage, coastImage];
    return images[Math.floor(Math.random() * images.length)];
  });

  useEffect(() => {
    if (!user || isFlagsLoading || profileState !== 'idle') return;

    if (!isFeatureEnabled('useDWProfiles')) {
      setProfileState('done');
      return;
    }

    const selectedProfile = sessionStorage.getItem('selectedProfile');
    if (selectedProfile) {
      setProfileState('done');
      return;
    }

    const cachedProfiles = sessionStorage.getItem('userProfiles');
    if (cachedProfiles) {
      try {
        const parsed = JSON.parse(cachedProfiles);
        setProfiles(parsed);
        setProfileState('selecting');
        return;
      } catch {}
    }

    const fetchProfiles = async () => {
      setProfileState('loading');
      try {
        const { data, error } = await supabase.functions.invoke('get-user-profiles', {
          body: { token: user.loginToken }
        });

        if (!error && data?.profiles) {
          sessionStorage.setItem('userProfiles', JSON.stringify(data.profiles));
          setProfiles(data.profiles);
          setProfileState('selecting');
        } else {
          console.error('Failed to fetch profiles:', error);
          setProfileState('done');
        }
      } catch (err) {
        console.error('Error fetching profiles:', err);
        setProfileState('done');
      }
    };

    fetchProfiles();
  }, [user, isFlagsLoading, isFeatureEnabled, profileState]);

  useEffect(() => {
    if (!user && !localStorage.getItem('user')) {
      setProfileState('idle');
      setProfiles([]);
      sessionStorage.removeItem('selectedProfile');
    }
  }, [user]);

  useEffect(() => {
    if (user && profileState === 'done') {
      fetchRoles();
    }
  }, [user, profileState, fetchRoles]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 w-full max-w-md">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  if (profileState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 w-full max-w-md">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (profileState === 'selecting') {
    return (
      <div key="profile-selector">
        <ProfileSelector 
          profiles={profiles} 
          backgroundImage={backgroundImage}
          onProfileSelected={async (profileId?: number) => {
            if (profileId) {
              // Clear all caches
              clearGroupsCache();
              clearUserInfoCache();
              clearUnitsCache();
              clearRolesCache();
              clearDigitalAssetsCache();
              clearMessageBoardCache();
              
              clearUnitDetailCache();
              queryClient.invalidateQueries({ queryKey: ['get-favorites-lists'] });
              queryClient.invalidateQueries({ queryKey: ['get-favorites-list-detail'] });
              queryClient.invalidateQueries({ queryKey: ['favorites-all-items'] });
              sessionStorage.removeItem('userProfiles');

              try {
                const { data, error } = await supabase.functions.invoke('switch-profile', {
                  body: { token: user.loginToken, profileUserId: profileId }
                });
                if (!error && data?.token) {
                  setProfileToken(data.token);
                } else {
                  console.error('Failed to switch profile:', error || data?.error);
                }
              } catch (err) {
                console.error('Error switching profile:', err);
              }
            }
            setProfileState('done');
            navigate('/', { replace: true });
          }}
        />
      </div>
    );
  }

  return <div key="main-content">{children}</div>;
};
